import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { Check } from 'lucide-react-native';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { trackEvent } from '../../lib/analytics';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { AnimatedSection, FadeIn } from '../animations';
import { Button } from '../primitives/Button';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import {
  GLASS_GRADIENT_END,
  GLASS_GRADIENT_START,
  GLASS_GRADIENT_STRONG,
  GlowBloom,
  softHalo,
} from './landingStyleKit';

const PerkItem = ({ perk, index }: { perk: string; index: number }) => (
  <FadeIn delay={400 + index * 100} translateY={15}>
    <View style={styles.feature}>
      <View style={styles.checkContainer}>
        <Check size={14} color={colors.primary[400]} strokeWidth={2.5} />
      </View>
      <Text variant="bodySmall" color={colors.text.secondary}>
        {perk}
      </Text>
    </View>
  </FadeIn>
);

/**
 * Where the signup CTAs lead. Defaults to signup so this stays a pure
 * presentational component: the landing screens own the capacity lookup, and an
 * unanswered gate must never render a waitlist it has not confirmed.
 */
export interface AccessGateCta {
  destination?: '/login' | '/waitlist';
}

export const PremiumCTASection = ({ destination = '/login' }: AccessGateCta = {}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const mp = useMixpanel();
  const { width } = useWindowDimensions();
  const isMobile = width < tokens.breakpoints.tablet;

  const perks = [
    t('landing.cta.perks.freeTrial'),
    t('landing.cta.perks.noCard'),
    t('landing.cta.perks.setup'),
  ];

  return (
    <View style={styles.section}>
      <Container style={styles.container}>
        <AnimatedSection delay={0}>
          <View style={styles.card}>
            <LinearGradient
              colors={GLASS_GRADIENT_STRONG}
              start={GLASS_GRADIENT_START}
              end={GLASS_GRADIENT_END}
              style={StyleSheet.absoluteFill}
            />
            <GlowBloom size={420} style={styles.cardGlow} />

            <View style={styles.cardBody}>
              <Text variant="h2" style={styles.title}>
                {t('landing.cta.title')}
              </Text>

              <FadeIn delay={100} translateY={15}>
                <Text variant="bodyLarge" color={colors.text.secondary} style={styles.subtitle}>
                  {t('landing.cta.subtitle')}
                </Text>
              </FadeIn>

              <FadeIn delay={200} translateY={15}>
                <Button
                  testID="landing_cta_section_login_cta"
                  variant="primary"
                  size="lg"
                  pill
                  style={styles.button}
                  onPress={() => {
                    trackEvent(mp, 'landing.login_cta_clicked', {
                      source: 'cta_section',
                      destination,
                    });
                    router.push(destination);
                  }}
                >
                  {t('landing.cta.primaryCta')}
                </Button>
              </FadeIn>

              <View style={[styles.features, isMobile && styles.featuresColumn]}>
                {perks.map((perk, index) => (
                  <PerkItem key={perk} perk={perk} index={index} />
                ))}
              </View>
            </View>
          </View>
        </AnimatedSection>
      </Container>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: colors.transparent,
    paddingVertical: Platform.select({ web: 80, default: 56 }),
    width: '100%',
  },
  container: {
    width: '100%',
  },
  card: {
    maxWidth: 940,
    width: '100%',
    alignSelf: 'center',
    borderRadius: tokens.radius['3xl'],
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
    overflow: 'hidden',
    ...softHalo,
  },
  cardGlow: {
    top: -120,
    alignSelf: 'center',
  },
  cardBody: {
    alignItems: 'center',
    paddingVertical: Platform.select({ web: 64, default: 44 }),
    paddingHorizontal: spacing.xl,
    gap: spacing.lg,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 28 }),
    lineHeight: Platform.select({ web: 48, default: 34 }),
    fontWeight: '800',
    textAlign: 'center',
    maxWidth: 580,
    color: colors.text.primary,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 520,
  },
  button: {
    minWidth: Platform.select({ web: 220, default: 200 }),
    marginTop: spacing.sm,
  },
  features: {
    flexDirection: 'row',
    gap: spacing.xl,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: spacing.md,
  },
  featuresColumn: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.md,
  },
  feature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  checkContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
